/* eslint-disable max-lines */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import { Eye, Copy, Check, AlertTriangle, Clock } from 'lucide-react';
import {
  OperationsSession,
  SESSION_STATUS_OPTIONS,
  STATUS_COLORS
} from '@utils/types/back-office/operations';

/* eslint-disable no-restricted-syntax -- Semantic session-status colors for icon tinting */
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Tooltip } from '@components/atoms/ui-kit/tooltip';
import {
  getErrorCategoryLabel,
  getErrorCategoryStyle
} from '@utils/types/errors/error-categories';

interface LiveActivityProps {
  sessions: OperationsSession[];
  totalItems: number;
  isLoading: boolean;
  onSelectSession: (session: OperationsSession) => void;
  onSelectMultiple: (sessions: OperationsSession[]) => void;
  onViewDetails: (session: OperationsSession) => void;
}

export default function LiveActivity({
  sessions,
  totalItems,
  isLoading,
  onSelectSession,
  onSelectMultiple,
  onViewDetails
}: LiveActivityProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const formatDuration = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return '-';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('just-now');
    if (diffMins < 60) return `${diffMins}m ${t('ago')}`;
    if (diffHours < 24) return `${diffHours}h ${t('ago')}`;
    return `${diffDays}d ${t('ago')}`;
  };

  // Isolated so its copied state doesn't live in the columns useMemo — otherwise
  // a single copy click rebuilds all column definitions and re-renders every row.
  const SessionIdCell = React.memo(({ id }: { id: string }) => {
    const [copied, setCopied] = useState(false);
    const { getColour: gc } = useThemeContext();
    const { t: tl } = useTranslation();
    const handleCopy = async (e: React.MouseEvent) => {
      e.stopPropagation();
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast.success(tl('copied-to-clipboard'));
      setTimeout(() => setCopied(false), 2000);
    };
    return (
      <div className="flex items-center gap-1 min-w-0">
        <span
          className="font-mono text-xs truncate"
          style={{ color: gc('normalText') }}
          title={id}
        >
          {id}
        </span>
        <button
          onClick={handleCopy}
          className="flex-shrink-0 p-0.5 rounded-2xl hover:bg-opacity-10"
          style={{ color: gc('inactiveText') }}
        >
          {copied ? (
            <Check className="h-3 w-3" style={{ color: '#22C55E' }} />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
      </div>
    );
  });

  const StatusBadge = ({
    status,
    errorMessage,
    errorCategory
  }: {
    status: string;
    errorMessage?: string | null;
    errorCategory?: string | null;
  }) => {
    const colors = STATUS_COLORS[status] || STATUS_COLORS['processing'];
    const badge = (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
        style={{
          backgroundColor: colors.bg,
          color: colors.text
        }}
      >
        {t(status)}
      </span>
    );

    // Show error details on hover for error sessions
    if (status === 'error' && errorMessage) {
      const categoryStyle = getErrorCategoryStyle(errorCategory);
      return (
        <Tooltip
          label={
            <div className="max-w-xs text-left">
              {errorCategory && (
                <span
                  className="block text-xs font-semibold uppercase mb-1"
                  style={{ color: categoryStyle.color }}
                >
                  {getErrorCategoryLabel(errorCategory)}
                </span>
              )}
              <span className="text-xs">{errorMessage}</span>
            </div>
          }
        >
          {badge}
        </Tooltip>
      );
    }

    return badge;
  };

  type Row = OperationsSession;

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'status',
        header: t('status'),
        field: 'status',
        accessorKey: 'status',
        minWidth: 100,
        maxWidth: 120,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        serverOperator: 'eq',
        suppressCellTitle: true,
        filterOptions: SESSION_STATUS_OPTIONS.map(s => ({
          label: t(s),
          value: s
        })),
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-1">
            <StatusBadge
              status={getValue() as string}
              errorMessage={row.original.error_message}
              errorCategory={row.original.error_category}
            />
            {row.original.is_stuck && (
              <span title={t('stuck')}>
                <Clock className="h-3.5 w-3.5" style={{ color: '#F97316' }} />
              </span>
            )}
            {row.original.is_stranded && (
              <span title={t('stranded')}>
                <AlertTriangle
                  className="h-3.5 w-3.5"
                  style={{ color: '#EF4444' }}
                />
              </span>
            )}
          </div>
        )
      },
      {
        id: 'session_id',
        header: t('session-id'),
        field: 'session_id',
        accessorKey: 'session_id',
        minWidth: 220,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => <SessionIdCell id={getValue() as string} />
      },
      {
        id: 'organization',
        header: t('customer'),
        field: 'organization_name',
        accessorKey: 'organization_name',
        filterKey: 'organization_name',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span className="font-medium">{String(getValue() ?? '')}</span>
        )
      },
      {
        id: 'filename',
        header: t('filename'),
        field: 'filename',
        accessorKey: 'filename',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => {
          const filename = getValue() as string | null;
          if (!filename) return '-';
          const truncated =
            filename.length > 20 ? `${filename.slice(0, 20)}...` : filename;
          return (
            <span
              className="text-xs"
              style={{ color: getColour('normalText') }}
              title={filename}
            >
              {truncated}
            </span>
          );
        }
      },
      {
        id: 'language',
        header: t('language'),
        field: 'language',
        accessorKey: 'language',
        minWidth: 80,
        maxWidth: 100,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <span
            className="text-xs px-1.5 py-0.5 rounded-2xl"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            {String(getValue() ?? '')}
          </span>
        )
      },
      {
        id: 'duration',
        header: t('duration'),
        field: 'duration',
        accessorKey: 'duration',
        minWidth: 90,
        maxWidth: 110,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        serverOperator: 'gte',
        cell: ({ getValue }) => formatDuration(getValue() as number | null)
      },
      {
        id: 'session_mode',
        header: t('mode'),
        field: 'session_mode',
        accessorKey: 'session_mode',
        minWidth: 80,
        maxWidth: 100,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <span
            className="text-xs font-medium capitalize"
            style={{ color: getColour('normalText') }}
          >
            {String(getValue() ?? '')}
          </span>
        )
      },
      {
        // Arrival in our system, and the list's default ordering. NOT
        // date_session_start: that is partly customer-supplied and often sits
        // in the future or days in the past, so sorting by it made the list
        // look unordered — recent uploads were neither at the top nor findable.
        id: 'created_at',
        header: t('created'),
        field: 'created_at',
        accessorKey: 'created_at',
        minWidth: 90,
        maxWidth: 110,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => (
          <span style={{ color: getColour('inactiveText') }}>
            {formatRelativeTime(String(getValue() ?? ''))}
          </span>
        )
      },
      {
        id: 'priority',
        header: t('priority'),
        field: 'priority',
        accessorKey: 'priority',
        minWidth: 70,
        maxWidth: 90,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const priority = getValue() as number | null;
          if (priority === null) return '-';
          return (
            <span
              className="text-xs font-medium"
              style={{ color: getColour('normalText') }}
            >
              {priority}
            </span>
          );
        }
      }
    ],
    [t, getColour, SessionIdCell]
  );

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'view-details',
        label: t('view-details'),
        icon: <Eye className="h-4 w-4" />,
        intent: 'primary',
        run: (row: Row) => {
          onViewDetails(row);
        }
      }
    ],
    [t, onViewDetails]
  );

  const handleRowClick = (row: Row) => {
    onSelectSession(row);
  };

  const handleSelectionChange = (selectedRows: Row[]) => {
    onSelectMultiple(selectedRows);
  };

  return (
    <div className="h-full">
      <DataGridV2<Row>
        data={sessions}
        columns={columns}
        actions={actions}
        enableRowSelection
        onRowSelectionChange={handleSelectionChange}
        enablePagination
        manualPagination
        enableSorting
        manualSorting={true}
        enableFiltering
        manualFiltering
        totalItems={totalItems}
        pageSize={25}
        pageSizeOptions={[25, 50, 100]}
        showColumnFilters
        filterMode="popover"
        height="100%"
        dense
        grid={false}
        striped
        loading={isLoading}
        onCellClick={handleRowClick}
        getRowId={row => row.session_id}
        virtualize
        estimatedRowHeight={37}
      />
    </div>
  );
}
